import { useEffect, useState } from "react";
import api, { fmtError } from "@/lib/api";
import { toast } from "sonner";
import { LifeBuoy, ArrowLeft, Loader2, X, Send, ShieldAlert, HelpCircle, CheckCircle2, Trash2, ShieldCheck, Users } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

export default function AdminTickets() {
  const { t } = useTranslation();
  const nav = useNavigate();
  const [tab, setTab] = useState("open");
  const [items, setItems] = useState(null);
  const [active, setActive] = useState(null);
  const [reply, setReply] = useState("");
  const [busy, setBusy] = useState(false);
  const [forbidden, setForbidden] = useState(false);

  const TAB_LABELS = {
    open: t("adminTickets.open"),
    answered: t("adminTickets.answered"),
    all: t("adminTickets.all"),
  };

  const load = async () => {
    setItems(null);
    try {
      const { data } = await api.get(`/admin/tickets?status=${tab}`);
      setItems(data);
    } catch (e) {
      if (e.response?.status === 403) setForbidden(true);
      else toast.error(fmtError(e.response?.data?.detail) || t("errors.generic"));
      setItems([]);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [tab]);

  const sendReply = async () => {
    if (!reply.trim()) { toast.error(t("adminTickets.replyRequired")); return; }
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/tickets/${active.id}/reply`, { message: reply.trim() });
      toast.success(data.email_sent ? t("adminTickets.sentToast") : t("adminTickets.sentNoEmailToast"));
      setActive(null);
      setReply("");
      load();
    } catch (e) { toast.error(fmtError(e.response?.data?.detail) || t("errors.generic")); }
    finally { setBusy(false); }
  };

  const deleteTicket = async (id) => {
    if (!window.confirm(t("adminTickets.confirmDelete"))) return;
    setBusy(true);
    try {
      await api.delete(`/admin/tickets/${id}`);
      toast.success(t("adminTickets.deletedToast"));
      setActive(null);
      load();
    } catch (e) { toast.error(fmtError(e.response?.data?.detail) || t("errors.generic")); }
    finally { setBusy(false); }
  };

  if (forbidden) {
    return (
      <div className="max-w-xl mx-auto px-4 sm:px-6 pt-12 pb-32 text-center">
        <h1 className="font-display font-black text-2xl">{t("admin.adminsOnly")}</h1>
        <p className="text-[#4A4F58] mt-2">{t("admin.noAccess")}</p>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 pt-6 pb-32">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <div className="flex items-center gap-3">
          <button onClick={() => nav("/admin")} className="btn-nb p-2.5 sm:p-3" data-testid="tickets-back">
            <ArrowLeft className="w-4 h-4 sm:w-5 sm:h-5" strokeWidth={2.5}/>
          </button>
          <div>
            <h1 className="font-display font-black text-2xl sm:text-4xl tracking-tight leading-none">{t("adminTickets.title")}</h1>
            <p className="text-xs sm:text-sm text-[#4A4F58] mt-0.5">{t("adminTickets.subtitle")}</p>
          </div>
        </div>
        <div className="grid grid-cols-2 gap-2 w-full sm:w-80">
          <Link to="/admin/verifications" className="btn-nb btn-nb-dark !px-3 !py-2 text-xs sm:text-sm font-bold justify-center text-center whitespace-nowrap">
            <ShieldCheck className="w-4 h-4 me-1.5 inline" /> ID Reviews
          </Link>
          <Link to="/admin/accounts" className="btn-nb btn-nb-dark !px-3 !py-2 text-xs sm:text-sm font-bold justify-center text-center whitespace-nowrap">
            <Users className="w-4 h-4 me-1.5 inline" /> Members
          </Link>
        </div>
      </div>

      <div className="mt-6 flex gap-2 glass-strong rounded-full p-1.5 shadow-soft">
        {["open", "answered", "all"].map((tk) => (
          <button key={tk} onClick={() => setTab(tk)} data-testid={`tickets-tab-${tk}`}
            className={`flex-1 py-2 rounded-full font-display font-bold text-sm transition-all ${tab === tk ? "bg-[#0F1115] text-white shadow-soft" : "text-[#4A4F58] hover:text-[#0F1115]"}`}>
            {TAB_LABELS[tk]}
          </button>
        ))}
      </div>

      <div className="mt-6 space-y-3" data-testid="admin-tickets-list">
        {items === null ? (
          <div className="card-nb p-6 text-center"><Loader2 className="w-5 h-5 animate-spin mx-auto"/></div>
        ) : items.length === 0 ? (
          <div className="card-nb p-8 text-center text-[#4A4F58]">{t("adminTickets.noTickets")}</div>
        ) : (
          items.map((it) => (
            <button key={it.id} onClick={() => { setActive(it); setReply(""); }} data-testid={`ticket-${it.id}`}
              className="card-nb w-full p-4 text-left hover:-translate-y-0.5 transition-all">
              <div className="flex items-center gap-2 flex-wrap">
                {it.type === "safety_concern"
                  ? <span className="tag-nb bg-[#FFE6E0] inline-flex items-center gap-1"><ShieldAlert className="w-3.5 h-3.5" strokeWidth={2.5}/>{t("adminTickets.safetyConcern")}</span>
                  : <span className="tag-nb bg-[#DDF5EA] inline-flex items-center gap-1"><HelpCircle className="w-3.5 h-3.5" strokeWidth={2.5}/>{t("adminTickets.question")}</span>}
                {it.category && <span className="tag-nb bg-[#FFF1D6]">{it.category}</span>}
                <span className={`tag-nb ${it.status === "open" ? "bg-[#FF7A6B] text-white" : "bg-[#DDF5EA]"}`}>{TAB_LABELS[it.status] || it.status}</span>
                <span className="text-[11px] font-mono text-[#7C828D] ms-auto">{it.created_at?.slice(0, 16).replace("T", " ")}</span>
              </div>
              <p className="mt-2 text-sm line-clamp-2 whitespace-pre-wrap">{it.message}</p>
              <p className="text-xs text-[#7C828D] mt-1.5 truncate">
                {t("adminTickets.from")}: <span className="font-semibold text-[#3D424B]">{it.user_name}</span> ({it.user_email})
              </p>
            </button>
          ))
        )}
      </div>

      {active && (
        <div className="fixed inset-0 bg-[#0A0B0D]/60 backdrop-blur-sm z-50 flex items-end sm:items-center justify-center p-4" onClick={() => setActive(null)}>
          <div className="card-nb p-6 pb-16 sm:pb-6 max-w-lg w-full bg-white animate-pop max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start justify-between mb-3">
              <div>
                <h3 className="font-display font-black text-xl">{active.user_name}</h3>
                <p className="text-xs text-[#7C828D]">{active.user_email}</p>
              </div>
              <button onClick={() => setActive(null)} className="btn-nb" data-testid="ticket-modal-close"><X className="w-4 h-4" strokeWidth={2.5}/></button>
            </div>

            <div className="flex items-center gap-2 flex-wrap mb-3">
              {active.type === "safety_concern"
                ? <span className="tag-nb bg-[#FFE6E0]">{t("adminTickets.safetyConcern")}</span>
                : <span className="tag-nb bg-[#DDF5EA]">{t("adminTickets.question")}</span>}
              {active.category && <span className="tag-nb bg-[#FFF1D6]">{active.category}</span>}
            </div>

            <div className="rounded-2xl bg-[#F4F2ED] p-4 text-sm whitespace-pre-wrap mb-4" data-testid="ticket-message">{active.message}</div>

            {active.reply ? (
              <div className="rounded-2xl border-2 border-[#14C8B0] bg-[#DDF5EA] p-4 mb-2" data-testid="ticket-existing-reply">
                <div className="flex items-center gap-1.5 text-xs font-display font-bold text-[#0F9D8F] mb-1.5">
                  <CheckCircle2 className="w-4 h-4" strokeWidth={2.5}/>
                  {t("adminTickets.repliedOn", { date: new Date(active.reply.replied_at).toLocaleString() })}
                </div>
                <p className="text-sm whitespace-pre-wrap">{active.reply.message}</p>
              </div>
            ) : (
              <>
                <textarea value={reply} onChange={(e) => setReply(e.target.value)} placeholder={t("adminTickets.replyPh")}
                  className="input-nb min-h-[110px] mb-2" data-testid="ticket-reply-input"/>
                <button onClick={sendReply} disabled={busy} className="btn-nb btn-nb-primary w-full" data-testid="ticket-send-reply-btn">
                  {busy ? <Loader2 className="w-4 h-4 animate-spin"/> : <Send className="w-4 h-4" strokeWidth={2.5}/>} {t("adminTickets.send")}
                </button>
                <p className="text-[11px] text-[#7C828D] mt-2 text-center">{t("adminTickets.sendHint")}</p>
              </>
            )}
            <button onClick={() => deleteTicket(active.id)} disabled={busy} className="btn-nb btn-nb-coral w-full mt-3" data-testid="ticket-delete-btn">
              <Trash2 className="w-4 h-4" strokeWidth={2.5}/> {t("adminTickets.delete")}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
